// src/client.ts
import createClient from "openapi-fetch";
var AntflyClient = class {
  constructor(config) {
    /**
     * Table operations
     */
    this.tables = {
      /**
       * List all tables
       */
      list: async (params) => {
        const { data, error } = await this.client.GET("/tables", {
          params: params ? { query: params } : void 0
        });
        if (error) throw new Error(`Failed to list tables: ${error.error}`);
        return data;
      },
      /**
       * Get table details and status
       */
      get: async (tableName) => {
        const { data, error } = await this.client.GET("/tables/{tableName}", {
          params: { path: { tableName } }
        });
        if (error) throw new Error(`Failed to get table: ${error.error}`);
        return data;
      },
      /**
       * Create a new table
       */
      create: async (tableName, config = {}) => {
        const { data, error } = await this.client.POST("/tables/{tableName}", {
          params: { path: { tableName } },
          body: config
        });
        if (error) throw new Error(`Failed to create table: ${error.error}`);
        return data;
      },
      /**
       * Drop a table
       */
      drop: async (tableName) => {
        const { error } = await this.client.DELETE("/tables/{tableName}", {
          params: { path: { tableName } }
        });
        if (error) throw new Error(`Failed to drop table: ${error.error}`);
        return true;
      },
      /**
       * Update schema for a table
       */
      updateSchema: async (tableName, config) => {
        const { data, error } = await this.client.PUT("/tables/{tableName}/schema", {
          params: { path: { tableName } },
          body: config
        });
        if (error) throw new Error(`Failed to update table schema: ${error.error}`);
        return data;
      },
      /**
       * Query a specific table
       */
      query: async (tableName, request) => {
        return this.performQuery("/tables/{tableName}/query", request, tableName);
      },
      /**
       * Execute multiple queries on a specific table
       */
      multiquery: async (tableName, requests) => {
        return this.performMultiquery("/tables/{tableName}/query", requests, tableName);
      },
      /**
       * Perform batch operations on a table
       */
      batch: async (tableName, request) => {
        const { data, error } = await this.client.POST("/tables/{tableName}/batch", {
          params: { path: { tableName } },
          // @ts-expect-error Our BatchRequest type allows any object shape for inserts
          body: request
        });
        if (error) throw new Error(`Batch operation failed: ${error.error}`);
        return data;
      },
      /**
       * Backup a table
       */
      backup: async (tableName, request) => {
        const { data, error } = await this.client.POST("/tables/{tableName}/backup", {
          params: { path: { tableName } },
          body: request
        });
        if (error) throw new Error(`Backup failed: ${error.error}`);
        return data;
      },
      /**
       * Restore a table from backup
       */
      restore: async (tableName, request) => {
        const { data, error } = await this.client.POST("/tables/{tableName}/restore", {
          params: { path: { tableName } },
          body: request
        });
        if (error) throw new Error(`Restore failed: ${error.error}`);
        return data;
      },
      /**
       * Lookup a specific key in a table
       * @param tableName - Name of the table
       * @param key - Key of the record to lookup
       * @param options - Optional parameters
       * @param options.fields - Comma-separated list of fields to include (e.g., "title,author,metadata.tags")
       */
      lookup: async (tableName, key, options) => {
        const { data, error } = await this.client.GET("/tables/{tableName}/lookup/{key}", {
          params: {
            path: { tableName, key },
            query: options?.fields ? { fields: options.fields } : void 0
          }
        });
        if (error) throw new Error(`Key lookup failed: ${error.error}`);
        return data;
      },
      /**
       * Scan keys in a table within a key range
       * Returns documents as an async iterable, streaming results as NDJSON.
       * @param tableName - Name of the table
       * @param request - Scan request with optional key range, field projection, and filtering
       * @returns AsyncGenerator yielding documents with their keys
       */
      scan: (tableName, request) => {
        const config = this.config;
        const authHeader = this.getAuthHeader();
        async function* scanGenerator() {
          const headers = {
            "Content-Type": "application/json",
            Accept: "application/x-ndjson"
          };
          if (authHeader) {
            headers.Authorization = authHeader;
          }
          Object.assign(headers, config.headers);
          const response = await fetch(`${config.baseUrl}/tables/${tableName}/lookup`, {
            method: "POST",
            headers,
            body: JSON.stringify(request || {})
          });
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Scan failed: ${response.status} ${errorText}`);
          }
          if (!response.body) {
            throw new Error("Response body is null");
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
              if (line.trim()) {
                yield JSON.parse(line);
              }
            }
          }
          if (buffer.trim()) {
            yield JSON.parse(buffer);
          }
        }
        return scanGenerator();
      },
      /**
       * Scan keys in a table and collect all results into an array
       * Convenience method that consumes the scan AsyncGenerator
       * @param tableName - Name of the table
       * @param request - Scan request with optional key range, field projection, and filtering
       * @returns Promise with array of all matching documents
       */
      scanAll: async (tableName, request) => {
        const results = [];
        for await (const doc of this.tables.scan(tableName, request)) {
          results.push(doc);
        }
        return results;
      }
    };
    /**
     * Index operations
     */
    this.indexes = {
      /**
       * List all indexes for a table
       */
      list: async (tableName) => {
        const { data, error } = await this.client.GET("/tables/{tableName}/indexes", {
          params: { path: { tableName } }
        });
        if (error) throw new Error(`Failed to list indexes: ${error.error}`);
        return data;
      },
      /**
       * Get index details
       */
      get: async (tableName, indexName) => {
        const { data, error } = await this.client.GET("/tables/{tableName}/indexes/{indexName}", {
          params: { path: { tableName, indexName } }
        });
        if (error) throw new Error(`Failed to get index: ${error.error}`);
        return data;
      },
      /**
       * Create a new index
       */
      create: async (tableName, config) => {
        const { error } = await this.client.POST("/tables/{tableName}/indexes/{indexName}", {
          params: { path: { tableName, indexName: config.name } },
          body: config
        });
        if (error) throw new Error(`Failed to create index: ${error.error}`);
        return true;
      },
      /**
       * Drop an index
       */
      drop: async (tableName, indexName) => {
        const { error } = await this.client.DELETE("/tables/{tableName}/indexes/{indexName}", {
          params: { path: { tableName, indexName } }
        });
        if (error) throw new Error(`Failed to drop index: ${error.error}`);
        return true;
      }
    };
    /**
     * User management operations
     */
    this.users = {
      /**
       * Get current authenticated user
       */
      getCurrentUser: async () => {
        const { data, error } = await this.client.GET("/users/me");
        if (error) throw new Error(`Failed to get current user: ${error.error}`);
        return data;
      },
      /**
       * List all users
       */
      list: async () => {
        const { data, error } = await this.client.GET("/users");
        if (error) throw new Error(`Failed to list users: ${error.error}`);
        return data;
      },
      /**
       * Get user details
       */
      get: async (userName) => {
        const { data, error } = await this.client.GET("/users/{userName}", {
          params: { path: { userName } }
        });
        if (error) throw new Error(`Failed to get user: ${error.error}`);
        return data;
      },
      /**
       * Create a new user
       */
      create: async (userName, request) => {
        const { data, error } = await this.client.POST("/users/{userName}", {
          params: { path: { userName } },
          body: request
        });
        if (error) throw new Error(`Failed to create user: ${error.error}`);
        return data;
      },
      /**
       * Delete a user
       */
      delete: async (userName) => {
        const { error } = await this.client.DELETE("/users/{userName}", {
          params: { path: { userName } }
        });
        if (error) throw new Error(`Failed to delete user: ${error.error}`);
        return true;
      },
      /**
       * Update user password
       */
      updatePassword: async (userName, newPassword) => {
        const { data, error } = await this.client.PUT("/users/{userName}/password", {
          params: { path: { userName } },
          body: { new_password: newPassword }
        });
        if (error) throw new Error(`Failed to update password: ${error.error}`);
        return data;
      },
      /**
       * Get user permissions
       */
      getPermissions: async (userName) => {
        const { data, error } = await this.client.GET("/users/{userName}/permissions", {
          params: { path: { userName } }
        });
        if (error) throw new Error(`Failed to get permissions: ${error.error}`);
        return data;
      },
      /**
       * Add permission to user
       */
      addPermission: async (userName, permission) => {
        const { data, error } = await this.client.POST("/users/{userName}/permissions", {
          params: { path: { userName } },
          body: permission
        });
        if (error) throw new Error(`Failed to add permission: ${error.error}`);
        return data;
      },
      /**
       * Remove permission from user
       */
      removePermission: async (userName, resource, resourceType) => {
        const { error } = await this.client.DELETE("/users/{userName}/permissions", {
          params: {
            path: { userName },
            query: { resource, resourceType }
          }
        });
        if (error) throw new Error(`Failed to remove permission: ${error.error}`);
        return true;
      }
    };
    this.config = config;
    this.client = this.buildClient();
  }
  /**
   * Build the Authorization header value from the auth config.
   * Returns undefined if no auth is configured.
   */
  getAuthHeader() {
    const auth = this.config.auth;
    if (!auth) return void 0;
    if ("type" in auth) {
      switch (auth.type) {
        case "basic":
          return `Basic ${btoa(`${auth.username}:${auth.password}`)}`;
        case "apiKey":
          return `ApiKey ${btoa(`${auth.keyId}:${auth.keySecret}`)}`;
        case "bearer":
          return `Bearer ${auth.token}`;
      }
    }
    return `Basic ${btoa(`${auth.username}:${auth.password}`)}`;
  }
  /**
   * Build the openapi-fetch client with current config.
   */
  buildClient() {
    const headers = {
      "Content-Type": "application/json",
      ...this.config.headers
    };
    const authHeader = this.getAuthHeader();
    if (authHeader) {
      headers.Authorization = authHeader;
    }
    return createClient({
      baseUrl: this.config.baseUrl,
      headers,
      bodySerializer: (body) => {
        if (typeof body === "string") {
          return body;
        }
        return JSON.stringify(body);
      }
    });
  }
  setAuth(authOrUsername, password) {
    if (typeof authOrUsername === "string" && password !== void 0) {
      this.config.auth = { username: authOrUsername, password };
    } else {
      this.config.auth = authOrUsername;
    }
    this.client = this.buildClient();
  }
  /**
   * Get cluster status
   */
  async getStatus() {
    const { data, error } = await this.client.GET("/status");
    if (error) throw new Error(`Failed to get status: ${error.error}`);
    return data;
  }
  /**
   * Private helper for query requests to avoid code duplication
   */
  async performQuery(path, request, tableName) {
    if (path === "/tables/{tableName}/query" && tableName) {
      const { data, error } = await this.client.POST("/tables/{tableName}/query", {
        params: { path: { tableName } },
        body: request
      });
      if (error) throw new Error(`Table query failed: ${error.error}`);
      return data;
    } else {
      const { data, error } = await this.client.POST("/query", {
        body: request
      });
      if (error) throw new Error(`Query failed: ${error.error}`);
      return data;
    }
  }
  /**
   * Private helper for multiquery requests to avoid code duplication
   */
  async performMultiquery(path, requests, tableName) {
    const ndjson = `${requests.map((request) => JSON.stringify(request)).join("\n")}
`;
    if (path === "/tables/{tableName}/query" && tableName) {
      const { data, error } = await this.client.POST("/tables/{tableName}/query", {
        params: { path: { tableName } },
        body: ndjson,
        headers: {
          "Content-Type": "application/x-ndjson"
        }
      });
      if (error) throw new Error(`Table multi-query failed: ${error.error}`);
      return data;
    } else {
      const { data, error } = await this.client.POST("/query", {
        body: ndjson,
        headers: {
          "Content-Type": "application/x-ndjson"
        }
      });
      if (error) throw new Error(`Multi-query failed: ${error.error}`);
      return data;
    }
  }
  /**
   * Global query operations
   */
  async query(request) {
    const data = await this.performQuery("/query", request);
    return data?.responses?.[0];
  }
  /**
   * Execute multiple queries in a single request
   */
  async multiquery(requests) {
    return this.performMultiquery("/query", requests);
  }
  /**
   * Private helper for Retrieval Agent requests to handle streaming and non-streaming responses
   */
  async performRetrievalAgent(request, callbacks) {
    const headers = {
      "Content-Type": "application/json",
      Accept: "text/event-stream, application/json"
    };
    const authHeader = this.getAuthHeader();
    if (authHeader) {
      headers.Authorization = authHeader;
    }
    Object.assign(headers, this.config.headers);
    const abortController = new AbortController();
    const response = await fetch(`${this.config.baseUrl}/agents/retrieval`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
      signal: abortController.signal
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Retrieval agent request failed: ${response.status} ${errorText}`);
    }
    if (!response.body) {
      throw new Error("Response body is null");
    }
    const contentType = response.headers.get("content-type") || "";
    const isJSON = contentType.includes("application/json");
    if (isJSON) {
      const result = await response.json();
      return result;
    }
    if (callbacks) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = "";
      (async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
              if (!line.trim()) {
                currentEvent = "";
                continue;
              }
              if (line.startsWith("event: ")) {
                currentEvent = line.slice(7).trim();
              } else if (line.startsWith("data: ")) {
                const data = line.slice(6).trim();
                try {
                  switch (currentEvent) {
                    case "classification":
                      if (callbacks.onClassification) {
                        callbacks.onClassification(JSON.parse(data));
                      }
                      break;
                    case "reasoning":
                      if (callbacks.onReasoning) {
                        callbacks.onReasoning(JSON.parse(data));
                      }
                      break;
                    case "clarification_required":
                      if (callbacks.onClarificationRequired) {
                        callbacks.onClarificationRequired(JSON.parse(data));
                      }
                      break;
                    case "filter_applied":
                      if (callbacks.onFilterApplied) {
                        callbacks.onFilterApplied(JSON.parse(data));
                      }
                      break;
                    case "search_executed":
                      if (callbacks.onSearchExecuted) {
                        callbacks.onSearchExecuted(JSON.parse(data));
                      }
                      break;
                    case "hit":
                      if (callbacks.onHit) {
                        callbacks.onHit(JSON.parse(data));
                      }
                      break;
                    case "generation":
                      if (callbacks.onGeneration) {
                        callbacks.onGeneration(JSON.parse(data));
                      }
                      break;
                    case "step_started":
                      if (callbacks.onStepStarted) {
                        callbacks.onStepStarted(JSON.parse(data));
                      }
                      break;
                    case "step_progress":
                      if (callbacks.onStepProgress) {
                        callbacks.onStepProgress(JSON.parse(data));
                      }
                      break;
                    case "step_completed":
                      if (callbacks.onStepCompleted) {
                        callbacks.onStepCompleted(JSON.parse(data));
                      }
                      break;
                    case "confidence":
                      if (callbacks.onConfidence) {
                        callbacks.onConfidence(JSON.parse(data));
                      }
                      break;
                    case "followup":
                      if (callbacks.onFollowup) {
                        callbacks.onFollowup(JSON.parse(data));
                      }
                      break;
                    case "eval":
                      if (callbacks.onEvalResult) {
                        callbacks.onEvalResult(JSON.parse(data));
                      }
                      break;
                    case "done":
                      if (callbacks.onDone) {
                        callbacks.onDone(JSON.parse(data));
                      }
                      return;
                    case "error":
                      if (callbacks.onError) {
                        callbacks.onError(JSON.parse(data));
                      }
                      throw new Error(data);
                  }
                } catch (e) {
                  console.warn("Failed to parse SSE data:", currentEvent, data, e);
                }
              }
            }
          }
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error("Retrieval agent streaming error:", error);
          }
        }
      })();
    }
    return abortController;
  }
  /**
   * Retrieval Agent - Unified retrieval pipeline with optional classification, generation, and eval
   * Supports pipeline mode (structured queries) and agentic mode (tool-calling with LLM)
   * Configure steps.classification, steps.answer, steps.eval to enable additional pipeline stages
   * @param request - Retrieval agent request with query, mode, and optional step configs
   * @param callbacks - Optional callbacks for SSE events (classification, reasoning, hit, answer, citation, confidence, followup_question, eval, done, error)
   * @returns Promise with RetrievalAgentResult (JSON) or AbortController (when streaming)
   */
  async retrievalAgent(request, callbacks) {
    return this.performRetrievalAgent(request, callbacks);
  }
  /**
   * Chat Agent - Multi-turn conversational retrieval with message history management.
   * Wraps the retrieval agent with automatic message accumulation.
   * @param userMessage - The user's message for this turn
   * @param config - Chat configuration (generator, table, indexes, etc.)
   * @param history - Previous conversation messages (pass result.messages from prior turns)
   * @param callbacks - Optional streaming callbacks including chat-specific events
   * @returns For streaming: { abortController, messages } where messages is a Promise.
   *          For non-streaming: { result, messages }
   */
  async chatAgent(userMessage, config, history = [], callbacks) {
    const request = {
      query: userMessage,
      queries: [
        {
          table: config.table,
          semantic_search: userMessage,
          indexes: config.semanticIndexes,
          limit: config.limit ?? 10
        }
      ],
      generator: config.generator,
      messages: [...history, { role: "user", content: userMessage }],
      max_iterations: config.maxIterations ?? 5,
      stream: !!callbacks,
      agent_knowledge: config.agentKnowledge
    };
    if (config.steps) {
      request.steps = config.steps;
    }
    if (callbacks) {
      let answerText = "";
      let resolveMessages;
      const messagesPromise = new Promise((resolve) => {
        resolveMessages = resolve;
      });
      const wrappedCallbacks = {
        ...callbacks,
        onGeneration: (chunk) => {
          answerText += chunk;
          callbacks.onGeneration?.(chunk);
        },
        onDone: (data) => {
          const updatedMessages2 = [
            ...history,
            { role: "user", content: userMessage },
            { role: "assistant", content: answerText }
          ];
          callbacks.onAssistantMessage?.(answerText);
          callbacks.onMessagesUpdated?.(updatedMessages2);
          callbacks.onDone?.(data);
          resolveMessages(updatedMessages2);
        }
      };
      const abortController = await this.performRetrievalAgent(
        request,
        wrappedCallbacks
      );
      return { abortController, messages: messagesPromise };
    }
    const result = await this.performRetrievalAgent(request);
    const updatedMessages = result.messages?.length ? result.messages : [
      ...history,
      { role: "user", content: userMessage },
      ...result.generation ? [{ role: "assistant", content: result.generation }] : []
    ];
    return { result, messages: updatedMessages };
  }
  /**
   * Query Builder Agent - Translates natural language into structured search queries
   * Uses an LLM to generate optimized Bleve queries from user intent
   * @param request - Query builder request with intent and optional table/schema context
   * @returns Promise with QueryBuilderResult containing the generated query, explanation, and confidence
   */
  async queryBuilderAgent(request) {
    const { data, error } = await this.client.POST("/agents/query-builder", {
      body: request
    });
    if (error) throw new Error(`Query builder agent failed: ${error.error}`);
    return data;
  }
  /**
   * Standalone evaluation for testing evaluators without running a query.
   * Evaluates a generated output against ground truth using LLM-as-judge metrics.
   * @param request - Eval request with evaluators, judge config, query, output, and ground truth
   * @returns Evaluation result with scores for each evaluator
   */
  async evaluate(request) {
    const { data, error } = await this.client.POST("/eval", {
      body: request
    });
    if (error) throw new Error(`Evaluation failed: ${error.error}`);
    return data;
  }
  /**
   * Get the underlying OpenAPI client for advanced use cases
   */
  getRawClient() {
    return this.client;
  }
};

// src/query-helpers.ts
function queryString(query, boost) {
  return {
    query,
    boost: boost ?? void 0
  };
}
function term(term2, field, boost) {
  return {
    term: term2,
    field,
    boost: boost ?? void 0
  };
}
function match(match2, field, options) {
  return {
    match: match2,
    field,
    analyzer: options?.analyzer,
    boost: options?.boost,
    prefix_length: options?.prefix_length,
    fuzziness: options?.fuzziness,
    operator: options?.operator
  };
}
function matchPhrase(matchPhrase2, field, options) {
  return {
    match_phrase: matchPhrase2,
    field,
    analyzer: options?.analyzer,
    boost: options?.boost,
    fuzziness: options?.fuzziness
  };
}
function prefix(prefix2, field, boost) {
  return {
    prefix: prefix2,
    field,
    boost: boost ?? void 0
  };
}
function fuzzy(term2, field, options) {
  return {
    term: term2,
    field,
    fuzziness: options?.fuzziness,
    prefix_length: options?.prefix_length,
    boost: options?.boost
  };
}
function numericRange(field, options) {
  return {
    field,
    min: options.min,
    max: options.max,
    inclusive_min: options.inclusive_min,
    inclusive_max: options.inclusive_max,
    boost: options.boost
  };
}
function dateRange(field, options) {
  return {
    field,
    start: options.start,
    end: options.end,
    inclusive_start: options.inclusive_start,
    inclusive_end: options.inclusive_end,
    datetime_parser: options.datetime_parser,
    boost: options.boost
  };
}
function matchAll(boost) {
  return {
    match_all: {},
    boost: boost ?? void 0
  };
}
function matchNone(boost) {
  return {
    match_none: {},
    boost: boost ?? void 0
  };
}
function boolean(options) {
  const result = {
    boost: options.boost
  };
  if (options.must && options.must.length > 0) {
    result.must = {
      conjuncts: options.must
    };
  }
  if (options.should && options.should.length > 0) {
    result.should = {
      disjuncts: options.should,
      min: options.minShouldMatch
    };
  }
  if (options.mustNot && options.mustNot.length > 0) {
    result.must_not = {
      disjuncts: options.mustNot
    };
  }
  if (options.filter) {
    result.filter = options.filter;
  }
  return result;
}
function conjunction(queries) {
  return {
    conjuncts: queries
  };
}
function disjunction(queries, min) {
  return {
    disjuncts: queries,
    min
  };
}
function docIds(ids, boost) {
  return {
    ids,
    boost: boost ?? void 0
  };
}
function geoDistance(field, location, distance, boost) {
  return {
    field,
    location,
    distance,
    boost: boost ?? void 0
  };
}
function geoBoundingBox(field, bounds, boost) {
  return {
    field,
    top_left: bounds.top_left,
    bottom_right: bounds.bottom_right,
    boost: boost ?? void 0
  };
}

// src/types.ts
var embedderProviders = [
  "termite",
  "ollama",
  "gemini",
  "vertex",
  "openai",
  "openrouter",
  "bedrock",
  "cohere",
  "mock"
];
var generatorProviders = [
  "ollama",
  "gemini",
  "openai",
  "anthropic",
  "vertex",
  "cohere",
  "termite",
  "openrouter"
];

// src/index.ts
var index_default = AntflyClient;
export {
  AntflyClient,
  boolean,
  conjunction,
  dateRange,
  index_default as default,
  disjunction,
  docIds,
  embedderProviders,
  fuzzy,
  generatorProviders,
  geoBoundingBox,
  geoDistance,
  match,
  matchAll,
  matchNone,
  matchPhrase,
  numericRange,
  prefix,
  queryString,
  term
};
